/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { TextInput } from '@/shared/ui';
import { cepMask } from '@/shared/utils';

export interface CepInputProps {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  label: string;
  error?: string;
  isLoading: boolean;
  /** Full `id` attribute for the underlying input (e.g. 'agency-postal_code', 'solo-postal-code'). */
  id: string;
  /** Optional `data-testid` for the underlying input. */
  testId?: string;
  /** Optional extra className applied to the input (e.g. a shared min-height token). */
  className?: string;
  /** Optional extra inline style applied to the input. */
  style?: React.CSSProperties;
}

/**
 * CEP (Brazilian postal code) text input with an inline loading spinner,
 * shared by the agency and solo-broker onboarding forms — they only differ
 * in id/testid/style, not in masking or layout behavior.
 *
 * @param props - CepInputProps
 * @returns The rendered CEP input with its optional loading spinner.
 */
export const CepInput = React.forwardRef<HTMLInputElement, CepInputProps>(
  ({ value, onChange, onBlur, label, error, isLoading, id, testId, className, style }, _ref) => {
    const handleChange = (raw: string) => {
      const { masked } = cepMask(raw);
      onChange(masked);
    };

    return (
      <div className="relative w-full">
        <TextInput
          id={id}
          label={label}
          value={value}
          onChange={e => handleChange(e.target.value)}
          onBlur={onBlur}
          aria-label={label}
          error={error}
          placeholder="00.000-000"
          maxLength={10}
          /* Numeric keyboard on mobile; avoids showing decimal/sign keys */
          inputMode="numeric"
          required
          className={className}
          style={style}
          data-testid={testId}
        />
        {isLoading && (
          <div className="absolute right-3 top-[42px] flex items-center pointer-events-none">
            <svg
              className="animate-spin h-4 w-4 text-(--seed-base)"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        )}
      </div>
    );
  },
);
CepInput.displayName = 'CepInput';

export default CepInput;
